<template>
    <div>

        <v-alert role="alert" type="error" icon="error" dense :value="!!error" class="mb-4">{{ error }}</v-alert>

        <announcement/>

        <template v-if="redirecting">
            <loading :message="$t('LTI_REDIRECTING')" />
        </template>
        
        <accessible-stepper v-else-if="activeStep > 0" v-model="activeStep" :steps="steps" class="elevation-0">
            <template #Login>
                <login-step @confirm="onLoginSuccess" />
            </template>
            <template #Select>
                <select-step :active="activeStep === 2" @cancel="onSelectCanceled" @confirm="onSelectConfirmed" />
            </template>
            <template #Link>
                <link-step :active="activeStep === 3" :course="selectedCourse" @cancel="onLinkCanceled" />
            </template>
        </accessible-stepper>

    </div>
</template>

<script lang="ts">
import Vue from "vue";
import Component from "vue-class-component";
import AccessibleStepper from '../../utils/AccessibleStepper.vue';
import LoginStep from './steps/LoginStep.vue';
import SelectStep from './steps/SelectStep.vue';
import Announcement from '@utils/Announcement.vue';
import { Watch } from "vue-property-decorator";
import { Course, ICourse, UserRole } from "@cyber-range/cyber-range-api-user-client";
import Config from "@/config";
import LinkStep from './steps/LinkStep.vue';
import Loading from '@utils/Loading.vue';
import { CreateUserParams } from "@/utils/userCreation/createUserParams";
import AddUserMethod from "@/interfaces/addUserMethod";
import { useErrorStore } from "@/stores/errorStore";
import { useApiClientStore } from "@/stores/apiClientStore";
import { useAuthenticationStore } from "@/stores/authenticationStore";
import { useUserStore } from "@/stores/userStore";
import { useEntitlementStore } from "@/stores/entitlementStore";
import { storeToRefs } from "pinia";

@Component({
    components: {  AccessibleStepper, Announcement, LoginStep, SelectStep, LinkStep, Loading }
})
export default class Lti extends Vue 
{
    get ltiIdentity() { return useAuthenticationStore().ltiIdentity; }
    get ltiToken() { return useAuthenticationStore().ltiToken; }
    get userApiClient() { return useApiClientStore().userApiClient; }
    get authenticationApiClient() { return useApiClientStore().authenticationApiClient; }
    getSsoToken = () => useAuthenticationStore().getSsoToken();
    login = (payload: { token: string, provider: string }) => useAuthenticationStore().login(payload);
    loadingBegin = () => useApiClientStore().loadingBegin();

    // TODO: Change to composition api
    error = storeToRefs(useErrorStore()).errorMessage;
    // END TODO
    
    activeStep = 0;
    steps = [ this.$t('LTI_LOGIN_STEP_TITLE'), this.$t('LTI_SELECT_STEP_TITLE'), this.$t('LTI_LINK_STEP_TITLE') ]

    redirecting = false;
    selectedCourse:ICourse = new Course();

    @Watch('activeStep')
    async onActiveStepChanged()
    {
        await useErrorStore().resetError();
    }

    async created()
    {
        if(!this.ltiIdentity.sub && !this.error)
        {
            useErrorStore().setError(new Error(this.$t('LTI_INVALID_IDENTITY').toString()))
        }

        if(this.ltiIdentity.courseApplicationId)
        {
            this.redirectToCourse()
        }
        else
        {
            let roles = this.ltiIdentity.roles;

            //Has to do a role check here because at this point we do not have the Cyber Range identity so a claim check is not possible.
            if(roles.includes(UserRole.Instructor) || roles.includes(UserRole.CourseAdmin) || roles.includes(UserRole.SuperAdmin))
            {
                // allow smoke tests to inject a pre-authenticated VCR token
                // via localStorage to skip the Google or Azure login step.
                const testToken = localStorage.getItem('vcr_test_token');
                if(testToken)
                {
                    try
                    {
                        if(await this.login({provider:'vcr', token: testToken}))
                        {
                            await useEntitlementStore().fetchClaims();
                            localStorage.removeItem('vcr_test_token');
                            
                            // Skip Login step and go straight to Select
                            this.activeStep = 2;
                            return;
                        }
                    }
                    catch
                    {
                        // If the injected token is invalid -> fall through to normal flow
                    }
                }

                this.activeStep = 1;
            }
            else
            {
                useErrorStore().setError(new Error(this.$t('LTI_MISSING_COURSE_APPLICATION').toString()))
            }
        }
        
    }

    async redirectToCourse()
    {
        try
        {
            this.redirecting = true;

            if(await this.login({provider:'vcr', token: this.ltiToken}))
            {
                if(this.ltiIdentity.roles.some(role => [UserRole.Instructor, UserRole.Student, UserRole.TA].includes(<any>role)))
                {
                    await useUserStore().createUser(new CreateUserParams(AddUserMethod.lti, {
                        ltiToken: this.ltiToken,
                        courseId: this.ltiIdentity.courseId,
                    }));
                }
                else
                {
                    let ssoToken = await this.getSsoToken();

                    // Disable all buttons while the user is being redirected to the destination.
                    this.loadingBegin();

                    window.location.href = new URL(`/courses/${this.ltiIdentity.courseId}?s=${ssoToken}`, Config.CYBER_RANGE_UI_BASE_URL).href;
                }
            }
        }
        finally
        {
            this.redirecting = false;
        }
    }

    onLoginSuccess(a)
    {
        this.activeStep++
    }

    onSelectCanceled()
    {
        this.activeStep--;
    }

    async onLinkCanceled()
    {
        this.activeStep--;
        this.selectedCourse = new Course();
    }
    async onSelectConfirmed(selectedCourse:ICourse)
    {
        this.activeStep++;
        this.selectedCourse = selectedCourse;
    }
}
</script>
